/**
 * Authentication Interceptor for Customer App
 * Handles authentication token persistence and 401 error handling
 */

// Store to track if we're already handling a logout to prevent loops
let isHandlingLogout = false;

/**
 * Get the current authentication token
 */
export const getAuthToken = () => {
  const token = localStorage.getItem('customerToken') || localStorage.getItem('token');
  
  // Validate token exists and is not empty
  if (token && token.trim().length > 0) {
    return token.trim();
  }
  
  return null;
};

/**
 * Set the authentication token
 */
export const setAuthToken = (token) => {
  if (token && token.trim().length > 0) {
    const cleanToken = token.trim();
    localStorage.setItem('customerToken', cleanToken);
    console.log('✅ Auth token stored successfully');
  }
};

/**
 * Clear the authentication token
 */
export const clearAuthToken = () => {
  localStorage.removeItem('customerToken');
  localStorage.removeItem('token');
  localStorage.removeItem('tempCustomerToken');
  localStorage.removeItem('user'); // Also clear user data
  console.log('🧹 Auth tokens cleared');
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

/**
 * Handle 401 Unauthorized error
 * 
 * IMPORTANT: This function checks if the token is expired.
 * - If token is expired: Clear it and redirect to login
 * - If other 401 error: Preserve token (could be temporary network issue)
 */
export const handle401Error = (errorMessage = '') => {
  // Prevent multiple simultaneous logout attempts
  if (isHandlingLogout) {
    console.log('⚠️ Already handling 401 error, skipping duplicate');
    return;
  }
  
  isHandlingLogout = true;
  
  // Check if this is a token expiration error
  const isTokenExpired = errorMessage && (
    errorMessage.toLowerCase().includes('token expired') ||
    errorMessage.toLowerCase().includes('jwt expired') ||
    errorMessage.toLowerCase().includes('expired token')
  );
  
  if (isTokenExpired) {
    // Token is definitely expired - clear it and force re-login
    console.error('❌ Token has expired - clearing and redirecting to login');
    clearAuthToken();
    
    // Dispatch event for token expiration
    window.dispatchEvent(new CustomEvent('auth:token-expired', {
      detail: { 
        reason: 'token_expired',
        message: 'Your session has expired. Please log in again.'
      }
    }));
    
    // Redirect to home after a short delay (browse as guest after session ends)
    setTimeout(() => {
      isHandlingLogout = false;
      window.location.href = '/home';
    }, 500);
  } else {
    // Other 401 error - preserve token (might be temporary)
    console.warn('⚠️ 401 Unauthorized - Preserving token (may be temporary issue)');
    console.warn('   User can retry the operation or manually log out if needed');
    
    // Dispatch custom event to notify app about the auth error
    window.dispatchEvent(new CustomEvent('auth:unauthorized', {
      detail: { 
        reason: 'auth_error',
        tokenPreserved: true,
        message: 'Authentication error occurred. Please try again or log out manually if the issue persists.'
      }
    }));
    
    // Reset the handling flag after a delay
    setTimeout(() => {
      isHandlingLogout = false;
    }, 500);
  }
};

/**
 * Enhanced fetch wrapper with authentication
 */
export const authenticatedFetch = async (url, options = {}) => {
  const token = getAuthToken();
  
  // Add authentication header if token exists
  const headers = {
    ...options.headers,
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Make the request
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  // Handle 401 Unauthorized
  if (response.status === 401) {
    console.error('❌ 401 Unauthorized response from:', url);
    
    // Try to get error message from response
    let errorMessage = '';
    try {
      const errorData = await response.clone().json();
      errorMessage = errorData.message || errorData.error || '';
    } catch (e) {
      // Failed to parse response, continue with empty message
    }
    
    handle401Error(errorMessage);
    throw new Error(errorMessage || 'Authentication failed - please log in again');
  }
  
  return response;
};

/**
 * Monitor localStorage for token changes
 */
export const monitorTokenChanges = (callback) => {
  const handleStorageChange = (e) => {
    if (e.key === 'customerToken' || e.key === 'token') {
      const newToken = e.newValue;
      console.log('🔔 Token changed in storage:', newToken ? 'Token present' : 'Token removed');
      callback(newToken);
    }
  };
  
  window.addEventListener('storage', handleStorageChange);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
};

/**
 * Persist token across app restarts
 */
export const ensureTokenPersistence = () => {
  const token = getAuthToken();
  
  if (token) {
    // Re-save token to ensure it's properly persisted
    setAuthToken(token);
    console.log('✅ Token persistence verified');
  } else {
    console.warn('⚠️ No token found - user may need to re-authenticate');
  }
};

/**
 * Setup global error handler for fetch requests
 * 
 * IMPORTANT: This handler checks for token expiration and handles it appropriately.
 * - Expired tokens: Cleared and user redirected to login
 * - Other 401 errors: Token preserved (might be temporary issue)
 */
export const setupGlobalErrorHandler = () => {
  // Store original fetch
  const originalFetch = window.fetch;
  
  // Override global fetch
  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);
      
      // Check for 401 on any fetch request
      if (response.status === 401 && !isHandlingLogout) {
        let url = '';
        if (typeof args[0] === 'string') {
          url = args[0];
        } else if (args[0] instanceof Request) {
          url = args[0].url;
        } else if (args[0] instanceof URL) {
          url = args[0].href;
        }
        
        // Don't trigger logout for login/register/guest endpoints
        if (url && 
            !url.includes('/auth/login') && 
            !url.includes('/auth/register') && 
            !url.includes('/auth/signup') &&
            !url.includes('/guest')) {
          console.warn('⚠️ Global 401 intercepted from:', url);
          
          // Try to get error message to check if token is expired
          let errorMessage = '';
          try {
            const errorData = await response.clone().json();
            errorMessage = errorData.message || errorData.error || '';
          } catch (e) {
            // Failed to parse response
          }
          
          // Handle the 401 error with the error message
          handle401Error(errorMessage);
        }
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  };
  
  console.log('✅ Global authentication error handler installed (with smart token expiration handling)');
};

export default {
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  isAuthenticated,
  handle401Error,
  authenticatedFetch,
  monitorTokenChanges,
  ensureTokenPersistence,
  setupGlobalErrorHandler,
};

